/**
 * Why a settled Moments search has nothing to show.
 *
 * Kept as a pure function rather than inlined into the MobX computed so the
 * precedence below is testable without standing up a LibraryManager. The store
 * getter (`LibraryManagerMultiLibrarySearch.zeroResultReason`) is a thin adapter
 * over this.
 */

export type ZeroResultReason =
  /** Nothing is selected in the library picker, so nothing was searched. */
  | 'no-scope'
  /** A selected library isn't ours to search — deleted, or never was. */
  | 'unreachable'
  /** A selected library's search failed for a reason that isn't access. */
  | 'search-failed'
  /** Every library in scope holds no media, so there was nothing to match. */
  | 'empty-scope'
  /** The scope's media is still uploading or processing, so nothing is searchable yet. */
  | 'processing'
  /** The scope holds media, but every row failed — nothing will ever be searchable. */
  | 'nothing-usable'
  /** The honest zero result: we searched indexed media, the query matched nothing. */
  | 'no-match';

export type ZeroResultInput = {
  /** How many library ids the user has selected, reachable or not. */
  selectedCount: number;
  /** Selected ids with no library of ours behind them. */
  unreachableCount: number;
  /** In-scope libraries whose search settled with an access error (403). */
  forbiddenCount: number;
  /**
   * In-scope libraries the search couldn't get an answer out of — its own request
   * errored with something other than access, or the library itself failed to load
   * so it never got asked.
   *
   * The second kind has to be counted here rather than ignored: such a library is
   * neither searchable nor unreachable, so leaving it out would shrink the scope
   * silently and let `no-match` blame the query for a search that never ran against
   * part of it.
   */
  failedCount: number;
  /** In-scope libraries that hold no media. */
  emptyCount: number;
  /** In-scope libraries the backend is still working through. */
  processingCount: number;
  /**
   * Whether media is on its way into a library in scope — anywhere between "bytes are
   * moving" and "the backend has finished with it".
   *
   * Separate from `processingCount` because it comes from a different place — the
   * upload notification rather than `stats.breakdown` — and it has to, twice over. The
   * row states a live upload sits in are the same ones a *failed* upload abandons
   * forever, so only a notification can tell those apart. And `stats` is refreshed at
   * upload success, while the backend row is still `empty`, so it cannot cover the
   * handover at all: there is a window where the transfer is done, nothing is queued
   * yet, and only the batch knows work is outstanding.
   */
  uploadInFlight: boolean;
  /** In-scope libraries holding media of which every row failed. */
  failedMediaOnlyCount: number;
  /** In-scope libraries we can actually search (a loaded store behind the id). */
  searchableCount: number;
  /**
   * Whether the user's library list has landed, so an empty selection means the
   * user really has nothing selected rather than that we don't know yet.
   *
   * Without this, a hard load of `/search?q=…` on a fresh account claims "No
   * libraries selected" for the moment before the demo library arrives and the
   * store auto-selects it.
   */
  scopeKnown: boolean;
  /**
   * Whether any library in scope is fetching right now.
   *
   * Nothing here may claim anything while this is true — not even the
   * scope-derived reasons that need no round trip. `results` returns null while any
   * library fetches, so `hasResults` reads false mid-flight, and without this guard
   * a reason would render over the top of `MomentGrid`'s skeletons *and* the results
   * the user was already looking at. `processing` made that constant rather than
   * occasional: library stats are never refetched when backend processing finishes,
   * so one upload leaves `mediaNotReadyCount` above zero for the rest of the
   * session, and every later search would flash "Your media is still processing"
   * before its results landed.
   */
  inFlight: boolean;
  /**
   * Whether every searchable library's search has actually run and come back —
   * data or an error, not merely "not fetching right now".
   *
   * The distinction matters more than it looks. A request is set synchronously on
   * keypress but the fetch behind it is debounced by 400ms, so for that window
   * nothing is in flight and nothing has been asked either. Reading that as
   * "settled with no results" is how the transcript suggestion once flashed a
   * full-page empty state in ahead of its own skeletons.
   */
  settled: boolean;
  /** Whether there is at least one result to render. */
  hasResults: boolean;
};

/**
 * Resolve the reason, most-specific first. Returns `null` when nothing here
 * claims the state — the caller renders the grid.
 *
 * **The order is the whole point.** "We couldn't search it" outranks "there was
 * nothing to search" outranks "nothing matched", because a claim about the query
 * is a lie when the query never ran against the library it's talking about.
 * Backwards, this produces the most annoying possible bug: telling someone their
 * words are wrong when the real problem is that a library vanished.
 *
 * The scope-derived reasons sit **above** the `settled` gate deliberately. Each is
 * answerable from state already in hand — the selection, the library list,
 * `stats.breakdown` — so making them wait on a network round trip would buy
 * nothing but the blank page this whole state exists to remove. That is the
 * ticket's worst case: an all-empty scope resolves instantly today, straight to
 * blank, and it should resolve instantly to an answer instead.
 *
 * `no-match` is last and it is genuinely rare, which is the point of everything
 * above it. Visual search runs no score threshold — `searchVisualEmbedding` is a
 * plain top-`limit` ANN query — and the Moments UI never sends `filters`, so any
 * scope with indexed media returns something however unrelated. Zero results
 * therefore almost always means *nothing was indexed to search*, not that the
 * words missed: media still processing, or media that all failed. Blaming the
 * query for those is the wrong answer twice over — wrong about the cause, and it
 * points the user at their own typing instead of at the thing they can act on.
 * What's left for `no-match` is the real case: transcript mode over media that
 * finished processing but has no speech in it.
 *
 * `unreachable` and `forbidden` collapse into one reason on purpose. They are the
 * same fact arriving by two routes — an id the library list never returned
 * (rehydrated from localStorage after the library was deleted or unshared), and
 * an id it did return but whose search 403s (access revoked mid-session). The
 * backend cannot tell "gone" from "not yours" in either case:
 * `checkUserAccessToLibrary` is a membership test, so both answer
 * `user.library.forbidden`. One hedged sentence covers both because one hedged
 * sentence is all the truth there is.
 */
export function resolveZeroResultReason(input: ZeroResultInput): ZeroResultReason | null {
  const {
    selectedCount,
    unreachableCount,
    forbiddenCount,
    failedCount,
    emptyCount,
    processingCount,
    uploadInFlight,
    failedMediaOnlyCount,
    searchableCount,
    scopeKnown,
    inFlight,
    settled,
    hasResults,
  } = input;

  // A search that is actually running outranks everything, including the reasons
  // that need no round trip. Mid-flight the grid owns the screen — skeletons, or the
  // previous results — and any claim made here would render over the top of them.
  if (inFlight) return null;

  // Results outrank every complaint. A search that found moments while one
  // library in scope was empty, missing, or broken has still answered the
  // question, and the moments are the answer — say nothing.
  if (hasResults) return null;

  if (selectedCount === 0) return scopeKnown ? 'no-scope' : null;
  if (unreachableCount > 0 || forbiddenCount > 0) return 'unreachable';

  // A transfer in progress outranks everything below, and sits above the `settled`
  // gate, because it cannot be wrong: a file still uploading is not searchable
  // whatever the search comes back with. It has to beat `empty-scope` in particular —
  // `stats` won't have caught up mid-upload, so the scope still looks empty, and "Add
  // media" is poor advice for someone whose media is already on its way.
  if (uploadInFlight) return 'processing';

  // Only claim "your libraries are empty" when that accounts for the entire scope.
  // A partly-empty scope did search real media and came back with nothing — the
  // empty library is a red herring there, and pointing at it would send the user off
  // to add media they may already have.
  //
  // Above the `settled` gate, and safe there: an all-empty scope cannot produce
  // results however long you wait, so answering before the fetch returns can't be
  // wrong. This is the ticket's worst case — it used to resolve instantly to a blank
  // page, and it should resolve instantly to an answer.
  if (searchableCount > 0 && emptyCount === searchableCount) return 'empty-scope';

  // Everything below is a claim about what the search found, so it needs the search
  // to have happened.
  //
  // `processing` in particular must not sit above this line, however answerable it
  // looks from `stats` alone. It fires on *any* in-flight media in scope, including a
  // scope whose other libraries would have returned results — and `inFlight` does not
  // protect it, because `inFlight` is false throughout the 400ms debounce before a
  // fetch even starts. Above the gate it rendered "Your media is still processing"
  // for that window on *every* search of the session, since nothing refetches stats
  // once media is queued. `nothing-usable` is grouped with it for the same reason:
  // both read stale `stats` to describe what a search would find.
  if (!settled) return null;

  // Media on its way is the most useful of these, and the only one that resolves by
  // itself — whether it is still transferring from the user's device or already with
  // the backend. Mutually exclusive with `empty-scope` above in the processing case (a
  // library with no media has nothing in flight); an upload can coexist with an empty
  // scope, since `stats` may not have caught up, and this claiming it first is right —
  // "add media" is poor advice while media is uploading.
  if (searchableCount > 0 && processingCount > 0) return 'processing';

  // Same quantifier as `empty-scope`, one step weaker: nothing in scope is
  // searchable, whether because a library is empty or because everything in it
  // failed. Kept separate because the offer differs — an empty library wants media
  // added, a library of failed uploads wants them looked at.
  if (searchableCount > 0 && emptyCount + failedMediaOnlyCount === searchableCount) {
    return 'nothing-usable';
  }

  if (failedCount > 0) return 'search-failed';

  // Nothing searchable, yet none of the guards above claimed it. Unreachable
  // today, but if a future scope filter drops libraries by some other route,
  // "nothing matched" would be the wrong thing to say — withhold rather than
  // assert something false.
  if (searchableCount === 0) return null;

  return 'no-match';
}
